/* eslint-disable @khanacademy/ts-no-error-suppressions */
import {plural, t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";
import {useDispatch, useSelector} from "react-redux";

import Button from "@khanacademy/wonder-blocks-button";
import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {styles as typographyStyles} from "@khanacademy/wonder-blocks-typography";
import {PermissionsInfo} from "@repo/shared-core";
import {
    a11yStyles,
    mediaQueries,
    standardTransition,
} from "@repo/shared-styles";

import type {ApolloGqlClient} from "#data-access/gql/apollo-types.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedTimeoutSpinner from "#shared-components-package/deprecated-timeout-spinner.tsx";

import * as sharedStyles from "../shared-styles.ts";
import type {
    FeedbackType,
    Post,
    PostKey,
    ServerPost,
    ViewMode,
} from "../types.ts";
import {getRespondButtonSrString as getRespondButtonScreenReaderString} from "../util.tsx";

import {fetchRepliesPage} from "./discussion-actions.ts";
import DiscussionInput from "./discussion-input.tsx";
import ConnectedDiscussionPost from "./discussion-post.tsx";
import type {
    RepliesPageStatus,
    State as StoreState,
} from "./discussion-reducer.ts";

type Props = Readonly<{
    postKey: PostKey;
    replies: Array<PostKey>;
    onReplyPostSuccess: (post: ServerPost, tab: FeedbackType) => void;
    tab: FeedbackType;
    topicSlug: string;
    resetNewPostKeyInfo: () => void;
    contentId: string;
    contentKind: string | null | undefined;
    restrictPosting: boolean;
    isLocked: boolean;
    isExpanded: boolean;
    containerId: string;
    gqlClient: ApolloGqlClient;
    // just to pass down to ConnectedDiscussionPost
    newPostKey: PostKey | null | undefined;
    moderatorMode?: boolean;
    viewMode: ViewMode;
    isMaximized?: boolean;
}>;

export const ReplyList = (props: Props): React.ReactElement | null => {
    const {
        postKey,
        replies,
        tab,
        topicSlug,
        resetNewPostKeyInfo,
        contentId,
        contentKind,
        restrictPosting,
        isLocked,
        isExpanded,
        containerId,
        gqlClient,
        viewMode,
        onReplyPostSuccess,
    } = props;

    const status: RepliesPageStatus | null = useSelector(
        (state: StoreState) => state.fetchingRepliesStatus[postKey],
    );
    const readyToRender =
        status === "complete" ||
        status === "partial" ||
        status === "loading_next";
    const willNotRender =
        !status ||
        status === "failed" ||
        (status === "complete" && !replies?.length);

    if (willNotRender && (!PermissionsInfo.isLoggedIn || restrictPosting)) {
        return null;
    }

    if (isExpanded && status === "loading_first") {
        return (
            <LoadingSpinner style={[styles.replyContainer, styles.loading]} />
        );
    }

    const outerRepliesContainerStyles = [
        styles.outerRepliesContainer,
        viewMode === "compact" && compactStyleSheet.outerRepliesContainer,
    ];

    return (
        <View
            style={
                isExpanded
                    ? outerRepliesContainerStyles
                    : [styles.collapsedReplyContainer, styles.displayNone]
            }
            id={containerId}
        >
            <View style={styles.innerRepliesContainer}>
                {readyToRender &&
                    replies?.map((replyKey, index) => (
                        <View key={replyKey}>
                            <View style={styles.replyContainer}>
                                {
                                    // @ts-expect-error - TS2739 - ConnectedDiscussionPost is missing the
                                    // following props: post, getRef, showPostedIn, and showParentQuestion
                                    <ConnectedDiscussionPost
                                        tab={tab}
                                        postKey={replyKey}
                                        newPostKey={props.newPostKey}
                                        resetNewPostKeyInfo={
                                            props.resetNewPostKeyInfo
                                        }
                                        topicSlug={props.topicSlug}
                                        restrictPosting={props.restrictPosting}
                                        moderatorMode={props.moderatorMode}
                                        viewMode={props.viewMode}
                                        isMaximized={props.isMaximized}
                                        autoFocusPostOnMount={
                                            index === 0 && isExpanded
                                        }
                                    />
                                }
                            </View>
                        </View>
                    ))}
            </View>
            {(status === "partial" || status === "loading_next") && (
                <LoadNextButton
                    postKey={postKey}
                    gqlClient={gqlClient}
                    status={status}
                />
            )}
            {PermissionsInfo.isLoggedIn && !restrictPosting && !isLocked && (
                <View style={styles.replyInputContainer}>
                    {/** HACK(kelsey): The post corresponding to
                    qa_expand_key will automatically show its replies.
                    However, we don't want to focus on the reply input which
                    is also shown. */}
                    {
                        <DiscussionInput
                            autoFocus={isExpanded && !replies?.length}
                            isReply={true}
                            tab={tab}
                            topicSlug={topicSlug}
                            feedbackKey={postKey}
                            resetNewPostKeyInfo={resetNewPostKeyInfo}
                            onPostSuccess={(
                                post: ServerPost,
                                tab: FeedbackType,
                            ) => {
                                // The server does not associate the result with
                                //   its parent post, so we need to do it here
                                //   in order to optimistically render the reply
                                // TODO(ned): Rename `questionKey` to `parentKey`
                                //   and populate for replies as well as answers
                                //   khanacademy.atlassian.net/browse/LC-1014
                                post.questionKey = postKey;
                                onReplyPostSuccess(post, tab);
                            }}
                            // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | undefined'.
                            contentKind={contentKind}
                            contentId={contentId}
                            showAvatar={viewMode === "compact"}
                        />
                    }
                </View>
            )}
        </View>
    );
};

const getComments = (count: number): string =>
    !count
        ? t`Comment`
        : plural(count, {
              one: "# comment",
              other: "# comments",
          });

export const CommentButton = (props: {
    post: Post;
    isExpanded: boolean;
    onClick: () => void;
    containerId: string;
    hideContributionButtons: boolean;
}): React.ReactElement | null => {
    const {post, isExpanded, onClick, containerId, hideContributionButtons} =
        props;

    const status: RepliesPageStatus | null = useSelector(
        (state: StoreState) => state.fetchingRepliesStatus[post.key],
    );

    // use actual reply length if we have it;
    // adjusts in cases where we have a replyCount but a different number of
    // replies if moderation has caused numbers to get out of sync
    const knownReplyCount =
        status && status === "complete"
            ? (post.replies?.length ?? post.replyCount) // replies shouldn't be null if status is complete, but better safe than sorry
            : post.replyCount;

    const labelSrString = getRespondButtonScreenReaderString(post, "comment");

    const commentButtonString: string =
        post.replyCount && post.replyCount > 0
            ? getComments(knownReplyCount ?? 0)
            : t`Comment`;

    const hasReplies = post.replyCount && post.replyCount > 0;

    if (!hasReplies && hideContributionButtons) {
        return null;
    }
    if (hasReplies || PermissionsInfo.isLoggedIn) {
        return (
            <>
                <Button
                    id={containerId}
                    kind="tertiary"
                    onClick={onClick}
                    aria-expanded={isExpanded}
                    aria-controls={containerId}
                >
                    {commentButtonString}
                </Button>
                <label className={css(a11yStyles.srOnly)} htmlFor={containerId}>
                    {labelSrString}
                </label>
            </>
        );
    }
    return (
        <>
            <Button id={containerId} kind="tertiary" onClick={onClick}>
                {commentButtonString}
            </Button>
            <label className={css(a11yStyles.srOnly)} htmlFor={containerId}>
                {t`Button navigates to signup page`}
            </label>
        </>
    );
};

const LoadNextButton = (props: {
    postKey: string;
    gqlClient: ApolloGqlClient;
    status: RepliesPageStatus;
}): React.ReactElement | null => {
    const {postKey, gqlClient, status} = props;

    const dispatch = useDispatch();
    const handleClick = () => {
        dispatch(fetchRepliesPage(postKey, gqlClient, {cursor}));
    };

    const cursor: string | null | undefined = useSelector(
        (state: StoreState) => state.posts[postKey].repliesCursor,
    );

    if (!cursor) {
        return null;
    }

    return status === "loading_next" ? (
        <LoadingSpinner
            style={[styles.replyContainer, styles.loading, styles.center]}
        />
    ) : (
        <Button
            kind="tertiary"
            aria-label="Load more comments"
            onClick={handleClick}
        >
            {t`Show more comments`}
        </Button>
    );
};

const LoadingSpinner = ({style}: {style: StyleType}) => (
    <View style={style}>
        <DeprecatedTimeoutSpinner
            size="xsmall"
            style={styles.spinner}
            sentryId="discussion_replies_load"
            description="Loading discussion post replies"
            sentryTeam="lems"
        />
        <span className={css(typographyStyles.LabelLarge)}>
            {t`Loading...`}
        </span>
    </View>
);

const styles = StyleSheet.create({
    replyContainer: {
        marginBottom: 2,
    },
    loading: {
        flexDirection: "row",
        paddingTop: 0,
        minHeight: 48,
        alignItems: "center",
    },
    center: {
        justifyContent: "center",
    },
    spinner: {
        marginRight: spacing.xSmall_8,
    },
    outerRepliesContainer: {
        backgroundColor: color.offWhite,
        marginTop: spacing.large_24,
        paddingTop: spacing.large_24,
        paddingLeft: spacing.large_24,
        paddingRight: spacing.large_24,
        maxHeight: "none",
        transition: `all ${standardTransition}`,

        [mediaQueries.xsOrSmaller]:
            sharedStyles.compactStyles.outerRepliesContainer,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    outerRepliesContainerWithAvatar: {
        // NOTE(slim): This is a special case not included with the rest of the
        // compact styles, because compact styles do not show avatars.
        [mediaQueries.xsOrSmaller]: {
            // Stretch the background to the left.
            // 16px spacing + 48px (the width of the avatar)
            marginLeft: -64,
        },
    },
    collapsedReplyContainer: {
        paddingLeft: spacing.large_24,
        paddingRight: spacing.large_24,
        maxHeight: 0,
        overflow: "hidden",
        transition: `all ${standardTransition}`,
        visibility: "hidden",
    },
    displayNone: {
        display: "none",
    },
    innerRepliesContainer: {
        marginBottom: 2,
    },
    replyInputContainer: {
        paddingBottom: spacing.large_24,
    },
});

const compactStyleSheet = StyleSheet.create(sharedStyles.compactStyles);

export default ReplyList;
